import { useState, useEffect, useRef } from 'react';
import { Button } from '../../Components/UI/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../Components/UI/popover';
import { Calendar } from '../../Components/UI/calendar';
import { DateInput } from '../Common/DateInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../Components/UI/select';
import { CheckIcon } from '@radix-ui/react-icons';
import { cn } from "../../Lib/Cn";
import { ChevronsUpDown } from 'lucide-react';
import { isSameDay, startOfDay, endOfDay, subDays, addDays, subWeeks, startOfWeek, subMonths, startOfMonth, endOfMonth, subYears, endOfWeek } from 'date-fns';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../Components/UI/tooltip';


const formatDate = (date, locale = "en-GB") => {
	if (!date) return "-";

	const inputDate = new Date(date);
	const currentYear = new Date().getFullYear();
	const inputYear = inputDate.getFullYear();

	const day = inputDate.getDate().toString().padStart(2, "0");
	let month = inputDate.toLocaleString(locale, { month: "short" });

	if (month.toLowerCase() === "sept") month = "Sep";

	if (inputYear === currentYear) {
		return `${day}-${month}`;
	} else {
		const shortYear = inputYear.toString().slice(-2).padStart(2, "0");
		return `${day}-${month}-${shortYear}`;
	}
};

const getDateAdjustedForTimezone = (dateInput) => {
	if (typeof dateInput === 'string') {
		const parts = dateInput.split('-').map((part) => parseInt(part, 10));
		return new Date(parts[0], parts[1] - 1, parts[2]);
	}
	return dateInput;
};

const presets = [
	{ name: 'today', label: 'Today' },
	{ name: 'yesterday', label: 'Yesterday' },
	{ name: 'tomorrow', label: 'Tomorrow' },
	{ name: 'last7', label: 'Last 7 days' },
	{ name: 'last14', label: 'Last 14 days' },
	{ name: 'last30', label: 'Last 30 days' },
	{ name: 'thisWeek', label: 'This Week' },
	{ name: 'lastWeek', label: 'Last Week' },
	{ name: 'thisMonth', label: 'This Month' },
	{ name: 'lastMonth', label: 'Last Month' },
	{ name: 'next7', label: 'Next 7 days' },
];

const expiresOnPresets = ['today', 'yesterday', 'tomorrow', 'last7', 'next7'];
const joinedOnPresets = ['today', 'yesterday', 'last7', 'last14', 'last30', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'];
const upgradedOnPresets = ['today', 'yesterday', 'last7', 'last14', 'last30', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'];


export const DatePickerWithRange = ({
	initialDateFrom,
	initialDateTo,
	initialCompareFrom,
	initialCompareTo,
	onUpdate,
	locale = 'en-GB',
	label,
	disableFuture = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [hasUserSelected, setHasUserSelected] = useState(false);
	const [hasCustomDateChanges, setHasCustomDateChanges] = useState(false);
	const [committedRange, setCommittedRange] = useState({
		fromDT: initialDateFrom ? getDateAdjustedForTimezone(initialDateFrom) : null,
		toDT: initialDateTo ? getDateAdjustedForTimezone(initialDateTo) : null
	});
	const [range, setRange] = useState({
		fromDT: initialDateFrom ? getDateAdjustedForTimezone(initialDateFrom) : null,
		toDT: initialDateTo ? getDateAdjustedForTimezone(initialDateTo) : null
	});
	const [rangeCompare, setRangeCompare] = useState(
		initialCompareFrom
			? {
				fromDT: startOfDay(new Date(initialCompareFrom)),
				toDT: initialCompareTo
					? startOfDay(new Date(initialCompareTo))
					: startOfDay(new Date(initialCompareFrom))
			}
			: undefined
	);
	const [isHoveringTrigger, setIsHoveringTrigger] = useState(false);

	const openedRangeRef = useRef();
	const triggerRef = useRef(null);
	const openedRangeCompareRef = useRef();
	const openedPresetRef = useRef();
	const [selectedPreset, setSelectedPreset] = useState(undefined);
	const [isSmallScreen, setIsSmallScreen] = useState(
		typeof window !== 'undefined' ? window.innerWidth < 960 : false
	);

	const isExpiresOn = label === "Expires on";
	const isJoinedOn = label === "Joined on";
	const isUpgradedOn = label === "Upgraded on";
	const visiblePresets = isExpiresOn
		? presets.filter(p => expiresOnPresets.includes(p.name)) : isJoinedOn
			? presets.filter(p => joinedOnPresets.includes(p.name)) : isUpgradedOn
				? presets.filter(p => upgradedOnPresets.includes(p.name)) : presets;

	useEffect(() => {
		const handleResize = () => setIsSmallScreen(window.innerWidth < 960);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const todayRange = (range) => {
		if (!range.fromDT || !range.toDT) return range;

		const fromDT = new Date(range.fromDT);
		const toDT = new Date(range.toDT);
		const today = startOfDay(new Date());

		const isTodayInvolved = isSameDay(fromDT, today) || isSameDay(toDT, today);

		if (isTodayInvolved) {
			return { fromDT: startOfDay(fromDT), toDT: endOfDay(toDT) };
		}

		return { fromDT, toDT };
	};

	const updateRange = (newRange) => {
		const normalized = todayRange(newRange || { fromDT: null, toDT: null });
		setRange(normalized);
		setHasCustomDateChanges(true);
		checkPreset();
	};

	const getPresetRange = (presetName) => {
		const preset = visiblePresets.find(({ name }) => name === presetName);
		if (!preset) throw new Error(`Unknown date range preset: ${presetName}`);

		let fromDT = new Date();
		let toDT = new Date();
		const first = startOfWeek(new Date());

		switch (preset.name) {
			case 'today':
				fromDT = startOfDay(fromDT);
				toDT = endOfDay(toDT);
				break;
			case 'yesterday':
				fromDT = startOfDay(subDays(fromDT, 1));
				toDT = endOfDay(subDays(toDT, 1));
				break;
			case 'tomorrow':
				fromDT = startOfDay(addDays(fromDT, 1));
				toDT = endOfDay(addDays(toDT, 1));
				break;
			case 'last7':
				fromDT = startOfDay(subDays(fromDT, 6));
				toDT = endOfDay(toDT);
				break;
			case 'next7':
				fromDT = startOfDay(fromDT);
				toDT = endOfDay(addDays(toDT, 6));
				break;
			case 'last14':
				fromDT = startOfDay(subDays(fromDT, 13));
				toDT = endOfDay(toDT);
				break;
			case 'last30':
				fromDT = startOfDay(subDays(fromDT, 29));
				toDT = endOfDay(toDT);
				break;
			case 'thisWeek':
				fromDT = first;
				toDT = endOfDay(toDT);
				break;
			case 'lastWeek':
				fromDT = startOfWeek(subWeeks(fromDT, 1));
				toDT = endOfWeek(subWeeks(toDT, 1));
				break;
			case 'thisMonth':
				fromDT = startOfMonth(fromDT);
				toDT = endOfDay(toDT);
				break;
			case 'lastMonth':
				fromDT = startOfMonth(subMonths(fromDT, 1));
				toDT = endOfMonth(subMonths(toDT, 1));
				break;
			default:
				fromDT = startOfDay(fromDT);
				toDT = endOfDay(toDT);
				break;
		}
		return { fromDT, toDT };
	};

	const setPreset = (preset) => {
		const range = getPresetRange(preset);
		updateRange(range);
		setSelectedPreset(preset);
		setCommittedRange(range);
		setHasUserSelected(true);

		onUpdate?.({ range, rangeCompare });

		if (rangeCompare) {
			const rangeCompareNew = {
				fromDT: subYears(range.fromDT, 1),
				toDT: range.toDT
					? subYears(range.toDT, 1)
					: undefined
			};
			setRangeCompare(rangeCompareNew);
		}

		setIsOpen(false);
		setIsHoveringTrigger(false);
		setTimeout(() => {
			if (triggerRef.current) {
				triggerRef.current.blur();
			}
		}, 0);
	};

	const checkPreset = () => {
		if (!hasUserSelected) {
			setSelectedPreset(undefined);
			return;
		}

		for (const preset of visiblePresets) {
			const presetRange = getPresetRange(preset.name);
			const normalizedRangeFrom = startOfDay(new Date(range.fromDT));
			const normalizedPresetFrom = startOfDay(new Date(presetRange.fromDT));
			const normalizedRangeTo = startOfDay(new Date(range.toDT ?? 0));
			const normalizedPresetTo = startOfDay(new Date(presetRange.toDT ?? 0));

			if (
				normalizedRangeFrom.getTime() === normalizedPresetFrom.getTime() &&
				normalizedRangeTo.getTime() === normalizedPresetTo.getTime()
			) {
				setSelectedPreset(preset.name);
				return;
			}
		}
		setSelectedPreset(undefined);
	};

	useEffect(() => {
		if (hasUserSelected) {
			checkPreset();
		}
	}, [hasUserSelected]);

	const areRangesEqual = (a, b) => {
		if (!a && !b) return true;
		if (!a || !b) return false;

		const getTime = (date) => (date instanceof Date ? date.getTime() : null);

		const fromA = getTime(a.fromDT);
		const fromB = getTime(b.fromDT);
		const toA = getTime(a.toDT);
		const toB = getTime(b.toDT);

		return fromA === fromB && toA === toB;
	};

	useEffect(() => {
		if (isOpen) {
			openedRangeRef.current = committedRange;
			openedRangeCompareRef.current = rangeCompare;
			openedPresetRef.current = selectedPreset;
			setRange({ ...committedRange });
		}
	}, [isOpen]);

	useEffect(() => {
		setHasUserSelected(false);
	}, []);

	useEffect(() => {
		const newFromDT = initialDateFrom ? getDateAdjustedForTimezone(initialDateFrom) : null;
		const newToDT = initialDateTo ? getDateAdjustedForTimezone(initialDateTo) : null;

		setCommittedRange({
			fromDT: newFromDT,
			toDT: newToDT
		});

		setRange({
			fromDT: newFromDT,
			toDT: newToDT
		});

		if (newFromDT || newToDT) {
			setHasUserSelected(true);
		}
	}, [initialDateFrom, initialDateTo]);

	const PresetButton = ({ preset, label, isSelected }) => (
		<Button
			className={cn('justify-between w-full', isSelected && 'bg-gray-100')}
			variant="ghost"
			onClick={() => setPreset(preset)}
		>
			<span>{label}</span>
			<span className={cn('opacity-0', isSelected && 'opacity-100')}>
				<CheckIcon width={18} height={18} />
			</span>
		</Button>
	);

	return (
		<Popover
			modal={true}
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					setRange({ ...committedRange });
					setRangeCompare(openedRangeCompareRef.current);
					setSelectedPreset(openedPresetRef.current);
					setHasCustomDateChanges(false);
					if (triggerRef.current) {
						triggerRef.current.blur();
					}
				}
				setIsOpen(open);
			}}
		>
			<Tooltip open={isHoveringTrigger && !isOpen}>
				<TooltipTrigger asChild>
					<PopoverTrigger asChild>
						<Button ref={triggerRef} variant="outline" className="bg-white hover:bg-white hover:border-sahi-primary-color filter-border"
							onMouseEnter={() => setIsHoveringTrigger(true)}
							onMouseLeave={() => setIsHoveringTrigger(false)}
							onPointerDown={() => {
								setIsHoveringTrigger(false);
								if (triggerRef.current) {
									triggerRef.current.blur();
								}
							}}>
							<div className="text-right">
								<div className="">
									{hasUserSelected && committedRange.fromDT && committedRange.toDT ? (
										isSameDay(committedRange.fromDT, committedRange.toDT) ? (
											formatDate(committedRange.toDT, locale)
										) : (
											<>
												{formatDate(committedRange.fromDT, locale)} to {formatDate(committedRange.toDT, locale)}
											</>
										)
									) : (
										label
									)}
								</div>
							</div>
							<div className="pl-1 opacity-60 ">
								<ChevronsUpDown width={24} />
							</div>
						</Button>
					</PopoverTrigger>
				</TooltipTrigger>
				<TooltipContent side="top" sideOffset={5}>
					<p>{label}</p>
				</TooltipContent>
			</Tooltip>

			<PopoverContent align="center" className="w-auto bg-white p-0 shadow-lg" style={{ backgroundColor: 'white' }}>
				<div className="flex p-4 bg-red rounded-lg">
					{!isSmallScreen && (
						<div className="flex flex-col items-start gap-1">
							<div className="flex w-full flex-col items-start gap-1">
								{visiblePresets.map((preset) => (
									<PresetButton
										key={preset.name}
										preset={preset.name}
										label={preset.label}
										isSelected={selectedPreset === preset.name}
									/>
								))}
							</div>
						</div>
					)}

					<div className="flex items-center">
						<div className="flex flex-col">
							<div className="flex flex-col lg:flex-row gap-2 px-3 justify-center items-center pb-4 lg:pb-0">
								<div className="flex flex-col gap-2">
									<div className="flex gap-2">
										<DateInput
											value={range.fromDT}
											onChange={(date) => {
												const toDate = range.toDT == null || date > range.toDT ? date : range.toDT;
												updateRange({ ...range, fromDT: date, toDT: toDate });
											}}
											disabled={disableFuture ? { after: new Date() } : undefined}
										/>
										<div className="py-1">to</div>
										<DateInput
											value={range.toDT}
											onChange={(date) => {
												const fromDate = date < range.fromDT ? date : range.fromDT;
												updateRange({ ...range, fromDT: fromDate, toDT: date });
											}}
											disabled={disableFuture ? { after: new Date() } : undefined}
										/>
									</div>

									{rangeCompare != null && (
										<div className="flex gap-2">
											<DateInput
												value={rangeCompare?.fromDT}
												onChange={(date) => {
													if (rangeCompare) {
														const compareToDate =
															rangeCompare.toDT == null || date > rangeCompare.toDT
																? date
																: rangeCompare.toDT;
														setRangeCompare((prev) => ({
															...prev,
															fromDT: date,
															toDT: compareToDate
														}));
													} else {
														setRangeCompare({ fromDT: date, toDT: new Date() });
													}
												}}
											/>
											<div className="py-1">-</div>
											<DateInput
												value={rangeCompare?.toDT}
												onChange={(date) => {
													if (rangeCompare && rangeCompare.fromDT) {
														const compareFromDate =
															date < rangeCompare.fromDT ? date : rangeCompare.fromDT;
														setRangeCompare({
															...rangeCompare,
															fromDT: compareFromDate,
															toDT: date
														});
													}
												}}
											/>
										</div>
									)}
								</div>
							</div>

							{isSmallScreen && (
								<Select
									value={selectedPreset}
									onValueChange={(value) => setPreset(value)}
								>
									<SelectTrigger className="w-[180px] mx-auto mb-2">
										<SelectValue placeholder="Select..." />
									</SelectTrigger>
									<SelectContent>
										{visiblePresets.map((preset) => (
											<SelectItem key={preset.name} value={preset.name}>
												{preset.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}

							<div>
								<Calendar
									mode="range"
									onSelect={(value) => {
										if (value?.from != null) {
											const newRange = {
												fromDT: value.from,
												toDT: value?.to || value.from
											};
											updateRange(newRange);
										}
									}}
									className="bg-white"
									selected={{ from: range.fromDT, to: range.toDT }}
									numberOfMonths={isSmallScreen ? 1 : 2}
									defaultMonth={
										new Date(
											new Date().setMonth(
												new Date().getMonth() - (isSmallScreen ? 0 : 1)
											)
										)
									}
									disabled={disableFuture ? { after: new Date() } : undefined}
									showOutsideDays={false}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="flex justify-end bg-white gap-2 py-4 pt-0 pr-4 rounded-b-lg">
					<Button
						variant="ghost"
						onClick={() => {
							updateRange({ fromDT: undefined, toDT: undefined });
							setRangeCompare(undefined);
							setSelectedPreset(undefined);
							setHasUserSelected(false);
							setHasCustomDateChanges(false);
							setIsOpen(false);
							setCommittedRange({ fromDT: null, toDT: null });
							onUpdate?.({
								range: { fromDT: null, toDT: null },
								rangeCompare: undefined
							});
							setIsHoveringTrigger(false);
							setTimeout(() => {
								if (triggerRef.current) {
									triggerRef.current.blur();
								}
							}, 0);
						}}
						className="border border-transparent hover:border-sahi-primary-color transition-colors duration-200"
					>
						Reset
					</Button>
					<Button
						variant="ghost"
						onClick={() => {
							setRange({ ...committedRange });
							setRangeCompare(openedRangeCompareRef.current);
							setSelectedPreset(openedPresetRef.current);
							setHasCustomDateChanges(false);
							setIsOpen(false);
							setIsHoveringTrigger(false);
							setTimeout(() => {
								if (triggerRef.current) {
									triggerRef.current.blur();
								}
							}, 0);
						}}
						className="border border-transparent hover:border-sahi-primary-color transition-colors duration-200"
					>
						Cancel
					</Button>
					{hasCustomDateChanges && (
						<Button
							onClick={() => {
								setIsOpen(false);
								if (
									!areRangesEqual(range, openedRangeRef.current) ||
									!areRangesEqual(rangeCompare, openedRangeCompareRef.current)
								) {
									setCommittedRange(range);
									onUpdate?.({ range, rangeCompare });
									setHasUserSelected(true);
									setHasCustomDateChanges(false);
								}
								setIsHoveringTrigger(false);
								setTimeout(() => {
									if (triggerRef.current) {
										triggerRef.current.blur();
									}
								}, 0);
							}}
						>
							Update
						</Button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};